// This is where the magic happens. This file exposes functions that allow
// us to convert openapi specs to fully functioning repositories. These
// repositories provide a layer of abstraction that allow components to
// interface with the backend, without actually having to know what kind of
// backend is being used.
import { OpenAPIV3 } from "openapi-types";
import { SupportedPropertyTypes } from "@/wc/registry/lib/service/repository/types.js";
import type {
    ActionPaths,
    IActionPathAllParameters,
    SupportedContentTypes,
    IActionPathConfiguration,
    IStekzSchema,
    IStekzFieldProperties,
} from "@/wc/registry/lib/service/repository/types.js";
import OpenAPIRepo from "@/wc/registry/lib/service/repository/openapi/base.js";
import * as openapi from "@/wc/registry/lib/service/repository/openapi/utils.js";
import { monitoring } from "@/lib/integrations/index.js";
import { has as lodashHas, set as lodashSet, get as lodashGet } from "lodash";
import { RepositoryCreationError } from "@/wc/registry/lib/service/repository/errors.js";

enum RequestMethods {
    GET = "get",
    POST = "post", // not actually supported, but we can show an upload field placeholder, so keeping it here.
    DELETE = "delete",
    PUT = "put",
    PATCH = "patch",
}

const schemaCombiners = ["oneOf", "anyOf", "allOf", "not"];
export const extensionProperties = [
    // Used to describe a field where the choices should be fetched from
    // a different endpoint. It could look like this:
    // "xChoiceOptions": {
    //   "href": "/countries",
    //   "method: "GET",
    //   "status_code": [ 200, 500 ]
    // }
    "xChoiceOptions",
    // Used to describe a field that can contain multiple translations.
    "xTranslatable",
];

const schemaExtensionProperties = ["format"];

const allowedProperties = [
    ...schemaCombiners,
    "default",
    "enum",
    "description",
    "examples",
    "format",
    "pattern",
    "title",
    "type",
    "maxLength",
    "minLength",
    "readOnly",
    "autocomplete",
];

export function getRequestMethods(): string[] {
    const requestMethodsArr = [];
    for (const item in RequestMethods) {
        requestMethodsArr.push(RequestMethods[item]);
    }
    return requestMethodsArr;
}

// Create a list of all the required parameters to make it easy for the
// repository to determine if all required information is there for a certain
// action to make a specific request.
export function getRequiredParametersFromPath(
    apiSpec: OpenAPIV3.Document,
    path: string
): string[] {
    const requiredParametersArr = [];
    const requestMethodsArr = getRequestMethods();

    requestMethodsArr.forEach((method) => {
        if (!(path in apiSpec["paths"])) {
            monitoring.error(`'${path}' does not exist in this openapi spec.`);
            return;
        }

        if (!(method in apiSpec["paths"][path])) {
            return;
        }

        if (!("parameters" in apiSpec["paths"][path][method])) {
            return;
        }

        apiSpec["paths"][path][method]["parameters"].forEach((parameter) => {
            if (
                parameter["required"] &&
                !requiredParametersArr.includes(parameter["name"])
            ) {
                requiredParametersArr.push(parameter["name"]);
            }
        });
    });

    // Order the required parameters to how they appear in the given path.
    const match = path.match(/{(.*?)}/g);
    if (match) {
        const pathParameters = match.map((match) => match.replace(/[{}]/g, ""));
        requiredParametersArr.sort(
            (a, b) => pathParameters.indexOf(a) - pathParameters.indexOf(b)
        );
    }

    return requiredParametersArr;
}

// Currently we support two types of parameters, the pathParameters and the
// queryParameters. These are extracted so that we can later determine where
// a certain parameter passed to the repository should be placed.
export function getAllParametersFromPath(
    apiSpec: OpenAPIV3.Document,
    path: string
): IActionPathAllParameters {
    const pathParametersArr = [];
    const queryParametersArr = [];
    const requestMethodsArr = getRequestMethods();

    requestMethodsArr.forEach((method) => {
        if (!(method in apiSpec["paths"][path])) {
            return;
        }
        if (!("parameters" in apiSpec["paths"][path][method])) {
            return;
        }
        const parameterArr = apiSpec["paths"][path][method]["parameters"];
        parameterArr.forEach((parameter) => {
            // add path parameters into the pathParametersArr
            if (
                parameter["in"] === "path" &&
                !pathParametersArr.includes(parameter["name"])
            ) {
                pathParametersArr.push(parameter["name"]);
            }
            // add query parameters into the queryParametersArr
            if (
                parameter["in"] === "query" &&
                !queryParametersArr.includes(parameter["name"])
            ) {
                queryParametersArr.push(parameter["name"]);
            }
        });
    });

    return {
        pathParameters: pathParametersArr,
        queryParameters: queryParametersArr,
    };
}

export function getRequestBodyContentType(
    apiSpec: OpenAPIV3.Document,
    path: string
): SupportedContentTypes {
    let requestBodyContentType: SupportedContentTypes = "application/json";
    const requestMethodsArr = getRequestMethods();
    requestMethodsArr.forEach((method) => {
        if (!(path in apiSpec["paths"])) {
            monitoring.error(`'${path}' does not exist in this openapi spec.`);
            return;
        }

        if (!(method in apiSpec["paths"][path])) {
            return;
        }

        const requestMethod = apiSpec?.["paths"]?.[path]?.[method];
        if (!requestMethod) {
            return undefined;
        }

        if ("requestBody" in requestMethod) {
            const requestContent = requestMethod?.["requestBody"]?.["content"];
            if (requestContent?.["application/json"]) {
                requestBodyContentType = "application/json";
            } else if (requestContent?.["application/x-www-form-urlencoded"]) {
                requestBodyContentType = "application/x-www-form-urlencoded";
            } else if (requestContent?.["multipart/form-data"]) {
                requestBodyContentType = "multipart/form-data";
            }
        } else {
            const statusCodesArr = ["200", "201"];
            const availableStatusCode = statusCodesArr.find((statusCode) => {
                return statusCode in apiSpec.paths[path][method].responses;
            });
            if (!availableStatusCode) {
                return undefined;
            }

            const responseContent =
                apiSpec.paths[path][method].responses[availableStatusCode]
                    .content;

            if (!responseContent) {
                return undefined;
            }

            if ("application/json" in responseContent) {
                requestBodyContentType = "application/json";
            } else if ("application/x-www-form-urlencoded" in responseContent) {
                requestBodyContentType = "application/x-www-form-urlencoded";
            } else if ("multipart/form-data" in responseContent) {
                requestBodyContentType = "multipart/form-data";
            }
        }
    });
    return requestBodyContentType;
}

function isStekzType(
    properties: object | IStekzFieldProperties
): properties is IStekzFieldProperties {
    return "type" in properties;
}

// Only allow certain properties to 'fall through', since these are the properties
// that we support in our StekzProperties.
function extractAllowedProperties(
    fieldProperties: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject
): IStekzFieldProperties | Partial<IStekzFieldProperties> {
    const properties = {};
    allowedProperties.forEach((allowedProperty) => {
        if (allowedProperty in fieldProperties) {
            properties[allowedProperty] = fieldProperties[allowedProperty];
        }
    });

    // These are properties that can be passed by the backend but they are not part of
    // the official OpenApi spec but instead they are part of the Stekz spec. These are
    // properties that are used to enrich or override the properties of a field.
    if (
        "schema_extra" in fieldProperties &&
        typeof fieldProperties["schema_extra"] === "object"
    ) {
        schemaExtensionProperties.forEach((schemaExtensionProperty) => {
            if (
                schemaExtensionProperty in
                (fieldProperties["schema_extra"] as object)
            ) {
                properties[schemaExtensionProperty] =
                    fieldProperties["schema_extra"][schemaExtensionProperty];
            }
        });
    }

    // These are non-default properties optionally passed by the backend to be
    // used to enrich the properties of the existing field. E.g. a backend
    // could tell the frontend that the options that can be shown for a given
    // field can be retrieved from a certain endpoint.
    extensionProperties.forEach((extensionProperty) => {
        if (extensionProperty in fieldProperties) {
            if (!("extensions" in properties)) {
                properties["extensions"] = {};
            }
            properties["extensions"][extensionProperty] =
                fieldProperties[extensionProperty];
        }
    });

    // When no "type" is set yet, converters only used this function to partially
    // extract information of what will in the end become a 'stekz type'.
    if (!isStekzType(properties)) {
        return properties;
    }

    // Append the type that is needed for the specific array. E.g. an array
    // of integers can be converted to "array-integer" and an array of strings
    // can be converted to "array-string". This allows us to easily utilize
    // different UI elements for different types.
    if (
        properties?.type &&
        properties.type === "array" &&
        "items" in fieldProperties &&
        "type" in fieldProperties.items
    ) {
        if (fieldProperties.items.type === "string") {
            properties.type = SupportedPropertyTypes.ArrayString;
        }
    }

    return properties;
}

function getSchemaCombiners(
    schema: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject
): string[] {
    return Object.keys(schema).filter((key) => schemaCombiners.includes(key));
}

// To simplify converting schemas this function can return the converter type
// that is required to convert the given schema (a.k.a. fieldProperties) to
// StekzProperties.
function determineRequiredConverterType(
    fieldProperties: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject
): string {
    if ("$ref" in fieldProperties) {
        return "hasSimpleSchemaReference";
    }

    if (
        "type" in fieldProperties &&
        fieldProperties["type"] === "array" &&
        "items" in fieldProperties &&
        "$ref" in fieldProperties["items"]
    ) {
        return "hasSimpleArraySchemaReference";
    }

    // Check the number of schemaCombiners in the fieldProperties.
    // E.g.
    // {
    //   allOf: [
    //     {
    //       '$ref': '#/components/schemas/ProducerBank'
    //     },
    //     {
    //       '$ref': '#/components/schemas/ConsumerBank'
    //     },
    //   ]
    // }
    // or in the case of a array schema sequences:
    // {
    //   items: {
    //     allOf: [
    //       {
    //         '$ref': '#/components/schemas/ProducerBank'
    //       },
    //       {
    //         '$ref': '#/components/schemas/ConsumerBank'
    //       }
    //     ]
    //   }
    // }
    const schemaCombiners = getSchemaCombiners(fieldProperties);
    if (schemaCombiners.length === 1) {
        const schemaCombiner = schemaCombiners[0];
        if (
            fieldProperties[schemaCombiner].length === 1 &&
            // If the schemaCombiner "not" is in the schema, avoid flattening
            // the schema through the converter associated with the
            // hasSchemaCombinerWithOneSchemaReference, since the "not" is
            // important to keep as it defines what data should not be submitted.
            !("not" in fieldProperties)
        ) {
            return "hasSchemaCombinerWithOneSchemaReference";
        } else {
            return "hasSchemaCombinerWithMultipleSchemaReferences";
        }
    }

    const arrayLevelSchemaCombiners = [];
    if ("items" in fieldProperties) {
        arrayLevelSchemaCombiners.push(
            ...getSchemaCombiners(fieldProperties.items)
        );
        if (arrayLevelSchemaCombiners.length === 1) {
            const schemaCombiner = arrayLevelSchemaCombiners[0];
            if (
                fieldProperties.items[schemaCombiner].length === 1 &&
                // If the schemaCombiner "not" is in the schema, avoid flattening
                // the schema through the converter associated with the
                // hasSchemaCombinerWithOneSchemaReference, since the "not" is
                // important to keep as it defines what data should not be submitted.
                !("not" in fieldProperties.items)
            ) {
                return "hasSchemaCombinerWithOneSchemaReference";
            } else {
                return "hasSchemaCombinerWithMultipleSchemaReferences";
            }
        }
    }

    return "hasSimpleSchema";
}

// Converts a schema reference into simple StekzProperties
// {
//   $ref: "#/components/schemas/ProducerBank",
// }
// Will be converted to
// {
//   title: "Bank Title",
//   type: string,
// }
function simpleSchemaReferenceConverter(
    apiSpec: OpenAPIV3.Document,
    fieldProperties: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject,

    action: string
): IStekzFieldProperties {
    const nestedSchema = openapi.getSchemaFromSchemaLocation(
        apiSpec,
        fieldProperties["$ref"]
    );

    // Taking the title from the field in the schema that refers to the
    // nested schema, if it exists. Otherwise fall back to the title of the
    // schema.
    const properties = {
        ...extractAllowedProperties(nestedSchema),
        ...extractAllowedProperties(fieldProperties),
        title:
            "title" in fieldProperties
                ? fieldProperties.title
                : nestedSchema.title || "",
        type: nestedSchema.type,
    };

    if (nestedSchema.type === "object") {
        properties["properties"] = convertSchemaIntoStekzProperties(
            apiSpec,
            nestedSchema,
            action
        );
    }

    return properties;
}

// Simplifies arrays with schema references into simple properties.
// E.g.
// {
//   title: "Bank Details",
//   type: "array",
//   items: {
//     $ref: "#/components/schemas/ProducerBank",
//   },
// }
// Will be converted to
// {
//   title: "Bank Details",
//   type: "array",
//   properties,
// }
function simpleArraySchemaReferenceConverter(
    apiSpec: OpenAPIV3.Document,
    fieldProperties: OpenAPIV3.ArraySchemaObject,
    action: string
): IStekzFieldProperties {
    const properties = {
        ...extractAllowedProperties(fieldProperties),
        title: "title" in fieldProperties ? fieldProperties.title : "",
        type: fieldProperties.type,
    };

    if ("items" in fieldProperties && "$ref" in fieldProperties.items) {
        const nestedSchema = openapi.getSchemaFromSchemaLocation(
            apiSpec,
            fieldProperties.items["$ref"]
        );

        if (nestedSchema) {
            properties["properties"] = convertSchemaIntoStekzProperties(
                apiSpec,
                nestedSchema,
                action
            );
        }
    }

    return properties;
}

// Flattens schemacombiners which contain schema references into simple
// properties.
// E.g.
// {
//   allOf: [
//     {
//       '$ref': '#/components/schemas/ProducerBank'
//     }
//   ]
// }
//
// Will be converted to
// {
//   name: 'Bank',
//   type: 'string'
// }
function schemaCombinerWithSchemaReferenceConverter(
    apiSpec: OpenAPIV3.Document,
    fieldProperties: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject,
    action: string
): IStekzFieldProperties {
    // Extract the name of the schemaCombiner that is present.
    const schemaCombiner = schemaCombiners.find(
        (schemaCombiner) => schemaCombiner in fieldProperties
    );
    // Only parsing references to schemas in schemaCombiners for now.
    if (!("$ref" in fieldProperties[schemaCombiner][0])) {
        return;
    }

    const nestedSchema = openapi.getSchemaFromSchemaLocation(
        apiSpec,
        fieldProperties[schemaCombiner][0]["$ref"]
    );

    // Taking the title from the field in the schema that refers to the
    // nested schema, if it exists. Otherwise fall back to the title of the
    // schema.
    const properties = {
        ...extractAllowedProperties(nestedSchema),
        ...extractAllowedProperties(fieldProperties),
        title:
            "title" in fieldProperties
                ? fieldProperties.title
                : nestedSchema.title || "",
        type: nestedSchema.type,
    };

    // Remove schema combiners if they are present in properties,
    // since they are not required anymore after simplifying.
    schemaCombiners.forEach((schemaCombiner) => {
        if (schemaCombiner in properties) {
            delete properties[schemaCombiner];
        }
    });

    if (nestedSchema.type === "object") {
        properties["properties"] = convertSchemaIntoStekzProperties(
            apiSpec,
            nestedSchema,
            action
        );
    }

    return properties;
}

// Sometimes there are fieldProperties with multiple schema combiners. E.g.
// E.g.
// {
//   allOf: [
//     {
//       '$ref': '#/components/schemas/ProducerBank'
//     },
//     {
//       '$ref': '#/components/schemas/ConsumerBank'
//     },
//   ]
// }
// These will be converted to
// {
//   allOf: [
//     IStekzSchema,
//     IStekzSchema
//   ]
// }
function schemaCombinerWithMultipleSchemaReferencesConverter(
    apiSpec: OpenAPIV3.Document,
    schema: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject,
    action: string
): Partial<IStekzFieldProperties> {
    const properties = {};

    schemaCombiners.forEach((schemaCombiner) => {
        // Check if the schemaCombiner is present as an array in the schema.
        const arrayLevelCombiner =
            "items" in schema && schemaCombiner in schema.items;
        const fieldLevelCombiner = schemaCombiner in schema;

        if (!arrayLevelCombiner && !fieldLevelCombiner) {
            return;
        }

        const combinerReferences = arrayLevelCombiner
            ? schema.items[schemaCombiner]
            : schema[schemaCombiner];
        combinerReferences.forEach((schemaRef: OpenAPIV3.BaseSchemaObject) => {
            if (!("$ref" in schemaRef)) {
                return;
            }

            if (typeof schemaRef.$ref !== "string") {
                return;
            }

            if (!(schemaCombiner in properties)) {
                properties[schemaCombiner] = [];
            }

            const nestedSchema = openapi.getSchemaFromSchemaLocation(
                apiSpec,
                schemaRef["$ref"]
            );
            const stekzProperties = convertSchemaIntoStekzProperties(
                apiSpec,
                nestedSchema,
                action
            );

            properties[schemaCombiner].push(stekzProperties);
        });
    });

    // The discriminator can be used to differentiate between the different
    // types of objects in the array.
    if (lodashHas(schema, "items.discriminator.propertyName")) {
        // Note, we're leaving out the parent 'items' here.
        lodashSet(
            properties,
            "discriminator.propertyName",
            lodashGet(schema, "items.discriminator.propertyName")
        );
    }

    return properties;
}

// The most simple converter, which extracts supported properties from the
// given schema.
function schemaConverter(
    _apiSpec: OpenAPIV3.Document,
    fieldProperties: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject,
    _action: string
): Partial<IStekzFieldProperties> {
    return extractAllowedProperties(fieldProperties);
}

// This object provides a map between pre-determined conditions and
// corresponding converter functions.
const converter = {
    hasSimpleSchemaReference: simpleSchemaReferenceConverter,
    hasSimpleArraySchemaReference: simpleArraySchemaReferenceConverter,
    hasSchemaCombinerWithOneSchemaReference:
        schemaCombinerWithSchemaReferenceConverter,
    hasSchemaCombinerWithMultipleSchemaReferences:
        schemaCombinerWithMultipleSchemaReferencesConverter,
    hasSimpleSchema: schemaConverter,
};

// Add all field-related requirements and properties to their respective fields.
export function convertSchemaIntoStekzProperties(
    apiSpec: OpenAPIV3.Document,
    schema: OpenAPIV3.SchemaObject,
    method: string
): IStekzSchema {
    if (!method) {
        throw Error(
            "convertSchemaIntoStekzProperties requires 'method' to be passed"
        );
    }

    const properties = {};
    if ("properties" in schema) {
        Object.entries(schema.properties).forEach(
            ([fieldName, fieldProperties]) => {
                const requiredConverterType =
                    determineRequiredConverterType(fieldProperties);

                properties[fieldName] = converter[requiredConverterType](
                    apiSpec,
                    fieldProperties,
                    method
                );
            }
        );
    } else {
        // Not all schemas define 'properties'. In the case of an endpoint which
        // returns a list of objects where these objects are of different types,
        // the root level schema contains a schema combiner in 'items' to
        // describe an array of schema combiners, such as 'oneOf' or 'anyOf'.
        // These types of schemas can be handled by the same converters.
        const requiredConverterType = determineRequiredConverterType(schema);
        return converter[requiredConverterType](apiSpec, schema, method);
    }

    // Only add required fields meta information to post/put/patch. Haven't found
    // a need to include it for get or delete yet.
    if (["post", "put", "patch"].includes(method) && "required" in schema) {
        schema.required.forEach((requiredField) => {
            properties[requiredField]["required"] = true;
        });
    }
    return properties;
}

// By checking out whether a certain set of properties is present on the
// action path, we deduct whether the action path is usable with searching
// features.
function isRequestSupportingSearch(path: IActionPathConfiguration) {
    if (!("path" in path)) {
        return false;
    }

    if (!("allParameters" in path)) {
        return false;
    }

    if (!("queryParameters" in path.allParameters)) {
        return false;
    }

    return path.allParameters.queryParameters.includes("search");
}

// By checking whether a certain set of properties is present on the
// in the action path, we deduct whether the action path is usable with
// searching features.
function isRequestSupportingSort(path: IActionPathConfiguration) {
    if (!("path" in path)) {
        return false;
    }

    if (!("allParameters" in path)) {
        return false;
    }

    if (!("queryParameters" in path.allParameters)) {
        return false;
    }

    return path.allParameters.queryParameters.includes("sort_by");
}

const requiredActionProperties = [
    "_actions",
    "_actions.type",
    "_actions.properties",
    "_actions.properties.href",
    "_actions.properties.method",
    "_actions.properties.name",
];

function isPolymorphicSchemaDescribingActionsPerItem(schema: IStekzSchema) {
    // Seems confusing, but it's the more efficient way of checking. Instead
    // of checking if every field is present by iterating over all schemas, we
    // exit early if one of the required fields does not exist.
    if (
        schema.oneOf.some((stekzFields) =>
            requiredActionProperties.some(
                (requiredProperty) => !lodashHas(stekzFields, requiredProperty)
            )
        )
    ) {
        return false;
    }

    // We can currently only deal with arrays of actions. If it is something
    // else, return early.
    if (
        schema.oneOf.some(
            (stekzFields) => stekzFields._actions.type !== "array"
        )
    ) {
        return false;
    }

    return true;
}

// By checking whether a certain set of properties is present on the
// schema, we deduct whether the schema is describing actions.
function isStekzSchemaDescribingActionsPerItem(
    schema: IStekzSchema,
    isPaginated: boolean
) {
    if (isPaginated) {
        schema = schema.items.properties;
    }

    if ("oneOf" in schema) {
        return isPolymorphicSchemaDescribingActionsPerItem(schema);
    }

    // Seems confusing, but it's the more efficient way of checking. Instead
    // of checking if every field is present by iterating over all schemas, we
    // exit early if one of the required fields does not exist.
    if (
        requiredActionProperties.some(
            (requiredProperty) => !lodashHas(schema, requiredProperty)
        )
    ) {
        return false;
    }

    return true;
}

// By checking out whether a certain set of properties is present on the
// schema, we deduct whether the schema is describing a paginated endpoint.
function isSchemaDescribingPagination(schema: OpenAPIV3.BaseSchemaObject) {
    if (!("properties" in schema)) {
        return false;
    }

    const requiredPaginationProperties = ["items", "total", "total_pages"];

    // Check if some required properties are missing
    if (
        requiredPaginationProperties.some(
            (property) => !(property in schema.properties)
        )
    ) {
        return false;
    }

    if (
        !(
            "type" in schema.properties.items &&
            schema.properties.items.type === "array"
        )
    ) {
        return false;
    }

    // Check each property to see if it describing an integer.
    for (const property of ["total", "total_pages"]) {
        if (!(property in schema.properties)) {
            return false;
        }
        const field = schema.properties[property];
        if (!("type" in field && field.type === "integer")) {
            return false;
        }
    }

    return true;
}

// Build an 'action path' for a specific path. It maps request methods to
// repository actions. E.g. a POST to /some-url/ will have an action path,
// where the (action) name is going to be 'create', where 'properties' will
// describe what fields /some-url/ expects.
function buildActionPath(
    apiSpec: OpenAPIV3.Document,
    path: string,
    method: string
) {
    const actionPath: IActionPathConfiguration = {
        method: method.toUpperCase(),
        requiredParameters: getRequiredParametersFromPath(apiSpec, path),
        allParameters: getAllParametersFromPath(apiSpec, path),
        path,
        requestBodyContentType: getRequestBodyContentType(apiSpec, path),
        info: {
            isPaginated: false,
            isSearchable: false,
            isSortable: false,
            hasItemActions: false,
        },
    };

    const schema = openapi.getSchemaForMethod(apiSpec, path, method);
    if (schema) {
        actionPath["properties"] = convertSchemaIntoStekzProperties(
            apiSpec,
            schema,
            method
        );
        actionPath.info.isPaginated = isSchemaDescribingPagination(schema);
        actionPath.info.isSearchable = isRequestSupportingSearch(actionPath);
        actionPath.info.isSortable = isRequestSupportingSort(actionPath);
        actionPath.info.hasItemActions = isStekzSchemaDescribingActionsPerItem(
            actionPath.properties,
            actionPath.info.isPaginated
        );
    }

    const responseSchema = openapi.getResponseSchemaForAction(
        apiSpec,
        path,
        method
    );
    if (responseSchema) {
        actionPath["response"] = convertSchemaIntoStekzProperties(
            apiSpec,
            responseSchema,
            method
        );
    }

    return actionPath;
}

// When the response of the GET request is a list, or a paginated response,
// we map it to the 'all' action path, so the resulting repository can request
// items through repository.objects.all(), otherwise, the request is likely
// describing a 'get' action for a specific item, which will be mapped to
// repository.objects.get().
function getActionForGETRequest(
    apiSpec: OpenAPIV3.Document,
    path: string,
    isPaginated: boolean
) {
    if (isPaginated) {
        return "all";
    }

    const response = openapi.getPathResponseContent(
        apiSpec,
        path,
        RequestMethods.GET
    );

    if ("type" in response.schema && response.schema.type === "array") {
        return "all";
    }

    return "get";
}

// To map routes from an openapi spec to a repository, paths from the
// spec are extracted and mapped to different actions.
export function buildActionPathsFromOpenApiSpec(
    apiSpec: OpenAPIV3.Document,
    repoPaths: (string | RegExp)[]
): ActionPaths {
    const actionPaths: ActionPaths = {};
    repoPaths.forEach((path) => {
        // When the passed path is a RegExp, try to find a matching path in the
        // apiSpec.
        if (path instanceof RegExp) {
            const matchedPath = Object.keys(apiSpec.paths).find((apiSpecPath) =>
                apiSpecPath.match(path)
            );
            if (matchedPath) {
                path = matchedPath;
            } else {
                monitoring.error(
                    `Could not find a path in the apiSpec that matches the RegExp '${path}'.`
                );
                return;
            }
        }

        if (!apiSpec.paths[path]) {
            // Throwing an error through the console here, to avoid not parsing
            // all the other repositories that might be defined in the config of
            // this app.
            monitoring.error(`'${path}' does not exist in this openapi spec.`);
            return;
        }

        const specPath = apiSpec.paths[path];
        if ("get" in specPath) {
            const actionPath = buildActionPath(
                apiSpec,
                path,
                RequestMethods.GET
            );
            const action = getActionForGETRequest(
                apiSpec,
                path,
                actionPath.info.isPaginated
            );
            actionPaths[action] = actionPath;
        }

        if ("put" in specPath) {
            actionPaths["update"] = buildActionPath(
                apiSpec,
                path,
                RequestMethods.PUT
            );
        }

        if ("patch" in specPath) {
            actionPaths["partialUpdate"] = buildActionPath(
                apiSpec,
                path,
                RequestMethods.PATCH
            );
        }

        if ("post" in specPath) {
            actionPaths["create"] = buildActionPath(
                apiSpec,
                path,
                RequestMethods.POST
            );
        }

        if ("delete" in specPath) {
            actionPaths["delete"] = buildActionPath(
                apiSpec,
                path,
                RequestMethods.DELETE
            );
        }
    });

    return actionPaths;
}

export async function createRepositoryFromOpenApiSpec(
    name: string,
    url: string,
    repoPaths: RegExp[] | string[]
): Promise<OpenAPIRepo> {
    const apiSpec = await openapi.getApiSpec(url);
    const actionPaths = buildActionPathsFromOpenApiSpec(apiSpec, repoPaths);
    if (Object.keys(actionPaths).length === 0) {
        throw new RepositoryCreationError(
            `Could not create repository for ${name}`
        );
    }
    return new OpenAPIRepo(name, actionPaths);
}
